import { Suspense, useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { ChevronRight, Heart, Loader2, Home, LayoutDashboard, LayoutGrid, LineChart, List, ListChecks, Receipt, Upload, ShoppingBasket, Zap, MessageSquareText, LogOut, Landmark, Repeat, ShieldCheck, Users, Settings, CalendarDays, Gauge } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { NotificationBell, useNotifications } from "@/components/notifications";

function AboutLink({ suffix = "", onNavigate }) {
  return <Link to="/about" onClick={onNavigate} title="About FruglFam" aria-label="About FruglFam" className="rounded-full p-2.5 text-rose-500 transition-colors hover:bg-rose-50" data-testid={`about-btn${suffix}`}><Heart className="h-4 w-4" /></Link>;
}

const RECEIPTS_NAV = new Set(["receipts", "ask", "household", "settings"]);
const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, id: "dashboard" },
  { to: "/cash-flow", label: "Cash flow", icon: CalendarDays, id: "cash-flow" },
  { to: "/transactions", label: "Transactions", icon: List, id: "transactions" },
  { to: "/imports", label: "Card statements", icon: Upload, id: "imports" },
  { to: "/bank-imports", label: "Bank statements", icon: Landmark, id: "bank-imports" },
  { to: "/receipts", label: "Receipts", icon: ShoppingBasket, id: "receipts" },
  { to: "/bills", label: "Electricity bills", icon: Zap, id: "bills" },
  { to: "/subscriptions", label: "Subscriptions", icon: Repeat, id: "subscriptions" },
  { to: "/insurance", label: "Insurance premiums", icon: ShieldCheck, id: "insurance" },
  { to: "/ask", label: "Wanna ChitChat?", icon: MessageSquareText, id: "ask" },
  { to: "/household", label: "Household", icon: Users, id: "household" },
  { to: "/settings", label: "Settings", icon: Settings, id: "settings" },
];

function useReminderCount(off) {
  const [count, setCount] = useState(0);
  const { pathname } = useLocation();
  useEffect(() => {
    if (off) return undefined;
    const load = () => api.get("/reminders").then((r) => setCount(r.data.count)).catch(() => {});
    load();
    window.addEventListener("reminders:changed", load);
    return () => window.removeEventListener("reminders:changed", load);
  }, [pathname, off]);
  return count;
}

function SideNav({ onNavigate, suffix = "", n }) {
  const { user, logout, receiptsOnly } = useAuth();
  const reminders = useReminderCount(receiptsOnly);
  const nav = receiptsOnly ? [{ to: "/", label: "Home", icon: Home, id: "home" }, ...NAV.filter((n) => RECEIPTS_NAV.has(n.id))] : NAV;
  return (
    <div className="flex h-full flex-col justify-between">
      <div>
        <div className="mb-8 px-2">
          <div className="flex items-center justify-between gap-1">
            <Link to="/" onClick={onNavigate} title="Go to home" className="inline-block font-heading text-lg font-extrabold tracking-tight transition-opacity hover:opacity-80" data-testid={`app-logo${suffix}`}>
              Frugl<span className="text-primary">Fam</span>
            </Link>
            {!suffix && <div className="flex items-center"><NotificationBell n={n} /><AboutLink /></div>}
          </div>
          <div className="text-xs text-muted-foreground">Spend smarter. Save more.</div>
        </div>
        <nav className="space-y-1">
          {[...nav, ...(suffix ? [{ to: "/about", label: "About FruglFam", icon: Heart, id: "about" }] : []), ...(user?.is_admin ? [{ to: "/admin/usage", label: "Admin · Usage", icon: Gauge, id: "admin-usage" }] : [])].map(({ to, label, icon: Icon, id }) => (
            <NavLink key={to} to={to} end={to === "/"} onClick={onNavigate} data-testid={`nav-${id}${suffix}`}
              className={({ isActive }) => `flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ${isActive ? "bg-primary text-primary-foreground" : "text-foreground/80 hover:bg-accent"}`}>
              <Icon className="h-4 w-4" /> {label}
              {id === "subscriptions" && reminders > 0 && (
                <span className="ml-auto rounded-full bg-amber-500 px-1.5 text-[11px] font-semibold text-white" data-testid={`reminder-badge${suffix}`}>{reminders}</span>
              )}
            </NavLink>
          ))}
        </nav>
      </div>
      <div className="border-t pt-4 px-2">
        <div className="text-sm font-medium" data-testid={`current-user-name${suffix}`}>{user?.name}</div>
        <div className="text-xs text-muted-foreground mb-3">{user?.email}</div>
        <button onClick={logout} data-testid={`logout-btn${suffix}`} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </div>
    </div>
  );
}

const TABS = [
  { to: "/", label: "Home", icon: Home, id: "home", on: (p) => p.pathname === "/" },
  { to: "/receipts", label: "Receipts", icon: Receipt, id: "receipts", on: (p) => p.pathname === "/receipts" && !["shopping", "prices"].includes(p.tab) },
  { to: "/receipts?tab=shopping", label: "List", icon: ListChecks, id: "shopping", on: (p) => p.pathname === "/receipts" && p.tab === "shopping" },
  { to: "/ask", label: "Rishi", icon: MessageSquareText, id: "rishi", on: (p) => p.pathname === "/ask" },
];

function MoreSheet({ open, onClose }) {
  const { user, logout, receiptsOnly } = useAuth();
  if (!open) return null;
  const links = [
    ...(receiptsOnly ? [] : NAV.filter((x) => !["receipts", "ask", "household", "settings", "dashboard"].includes(x.id))),
    { to: "/receipts?tab=prices", label: "Price tracker", icon: LineChart, id: "prices" },
    { to: "/household", label: "Household", icon: Users, id: "household" },
    { to: "/settings", label: "Settings", icon: Settings, id: "settings" },
    { to: "/about", label: "About FruglFam", icon: Heart, id: "about" },
    ...(user?.is_admin ? [{ to: "/admin/usage", label: "Admin · Usage", icon: Gauge, id: "admin-usage" }] : []),
  ];
  return (
    <div className="fixed inset-0 z-50 lg:hidden" data-testid="more-sheet">
      <button className="absolute inset-0 bg-black/40 animate-in fade-in-0" onClick={onClose} aria-label="Close menu" data-testid="more-sheet-backdrop" />
      <div className="absolute inset-x-0 bottom-0 rounded-t-3xl bg-card px-4 pt-2 shadow-2xl animate-in slide-in-from-bottom duration-200" style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom))" }}>
        <div className="mx-auto mb-3 h-1.5 w-12 rounded-full bg-slate-300" />
        <div className="mb-3 flex items-center gap-3 px-1">
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary/10 font-heading text-base font-bold text-primary">{(user?.name || "?").slice(0, 1)}</span>
          <div className="min-w-0"><div className="truncate font-heading text-base font-semibold" data-testid="current-user-name-mobile">{user?.name}</div><div className="truncate text-sm text-muted-foreground">{user?.email}</div></div>
        </div>
        <nav className="divide-y overflow-hidden rounded-2xl border">
          {links.map(({ to, label, icon: Icon, id }) => (
            <Link key={id} to={to} onClick={onClose} data-testid={`more-${id}`} className="flex min-h-[52px] items-center gap-3 px-4 text-[15px] font-medium active:bg-muted">
              <Icon className="h-5 w-5 text-muted-foreground" />{label}<ChevronRight className="ml-auto h-4 w-4 text-muted-foreground" />
            </Link>
          ))}
        </nav>
        <button onClick={() => { onClose(); logout(); }} data-testid="logout-btn-mobile" className="mt-3 flex min-h-[52px] w-full items-center justify-center gap-2 rounded-2xl border text-[15px] font-medium text-red-600 active:bg-red-50">
          <LogOut className="h-5 w-5" /> Sign out
        </button>
      </div>
    </div>
  );
}

function TabBar({ onMore, moreOpen }) {
  const loc = useLocation();
  const p = { pathname: loc.pathname, tab: new URLSearchParams(loc.search).get("tab") };
  const moreActive = moreOpen || !TABS.some((t) => t.on(p));
  const item = "flex min-h-[56px] flex-1 flex-col items-center justify-center gap-0.5 text-[11px] font-semibold transition-colors";
  return (
    <nav className="fixed inset-x-0 bottom-0 z-40 border-t bg-card/95 backdrop-blur-md lg:hidden" style={{ paddingBottom: "env(safe-area-inset-bottom)" }} data-testid="bottom-nav">
      <div className="mx-auto flex max-w-lg px-1">
        {TABS.map(({ to, label, icon: Icon, id, on }) => {
          const active = !moreOpen && on(p);
          return (
            <Link key={id} to={to} data-testid={`bottom-nav-${id}`} aria-current={active ? "page" : undefined} className={`${item} ${active ? "text-primary" : "text-muted-foreground"}`}>
              <span className={`flex h-7 w-12 items-center justify-center rounded-full transition-colors ${active ? "bg-primary/10" : ""}`}><Icon className="h-[22px] w-[22px]" strokeWidth={active ? 2.4 : 2} /></span>{label}
            </Link>
          );
        })}
        <button onClick={onMore} data-testid="bottom-nav-more" aria-current={moreActive ? "page" : undefined} className={`${item} ${moreActive ? "text-primary" : "text-muted-foreground"}`}>
          <span className={`flex h-7 w-12 items-center justify-center rounded-full ${moreActive ? "bg-primary/10" : ""}`}><LayoutGrid className="h-[22px] w-[22px]" /></span>More
        </button>
      </div>
    </nav>
  );
}

export default function Layout() {
  const [more, setMore] = useState(false);
  const n = useNotifications();
  const { pathname, search } = useLocation();
  useEffect(() => setMore(false), [pathname, search]);
  return (
    <div className="min-h-screen">
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 border-r bg-card p-4 lg:block"><SideNav n={n} /></aside>
      <header className="sticky top-0 z-30 flex items-center justify-between border-b bg-background/90 px-4 pb-2 backdrop-blur-md lg:hidden" style={{ paddingTop: "calc(0.5rem + env(safe-area-inset-top))", paddingLeft: "max(1rem, env(safe-area-inset-left))", paddingRight: "max(1rem, env(safe-area-inset-right))" }}>
        <Link to="/" className="font-heading text-xl font-extrabold tracking-tight" data-testid="app-logo-header">Frugl<span className="text-primary">Fam</span></Link>
        <div className="flex items-center gap-1">
          <NotificationBell n={n} suffix="-mobile" />
          <AboutLink suffix="-mobile" />
        </div>
      </header>
      <main className="lg:pl-64"><div className="mx-auto max-w-7xl px-4 pb-[calc(6rem+env(safe-area-inset-bottom))] pt-4 sm:px-8 sm:pt-8 lg:py-10"><Suspense fallback={<div className="flex min-h-[40vh] items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>}><Outlet /></Suspense></div></main>
      <TabBar onMore={() => setMore((o) => !o)} moreOpen={more} />
      <MoreSheet open={more} onClose={() => setMore(false)} />
    </div>
  );
}
